// InlineLink — a link inside agent-written markdown, pure.
//
// The agent links the resources it names as ordinary markdown links to their
// client route (`[Algorithm Design](/library/{id})`, see the "Linking resources
// in your replies" section of the chat system prompt). The href is PARSED at the
// boundary by the host's `ResolveInlineLink` (app/route/resourceLink.ts) into an
// `InlineLinkTarget`; this module only projects that target to a view:
//   resource — the SAME chip as the Author's @-mention (one reference
//              vocabulary for user and agent); clicking it opens the resource in
//              the right panel via the chat intent `requested_resource_open`
//              (→ the right panel), never a page navigation.
//   external — an http(s) link leaves the app: new tab, opener severed.
//   conversation — a saved conversation (`/c/{id}`), for surfaces that open
//              conversations (the Journal); the chat leaves it plain.
//   plain    — anything else (an id not in the library, an unsupported scheme)
//              renders as its text, never a dead link.
//
// Two chip labels: the chat's chip names the resource by its TITLE (as the
// @-mention does); prose that is read rather than conversed in (the Journal)
// keeps the writer's own LINK TEXT, so a sentence reads as written
// (`selectLinkTextChipView`).

export type { InlineLinkTarget, ResolveInlineLink };
export {
    InlineLink,
    LinkChip,
    linkGlyph,
    selectInlineLinkView,
    selectLinkTextChipView,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { ReactNode } from "react";
import { toComposerResourceRef } from "../../../app/AppShellHost/constants.js";
import { ResourceMentionChip } from "../../Composer/PowerComposer/ResourceMentionChip.js";
import type { ComposerResourceRef } from "../../Composer/PowerComposer/types.js";
import { Icon, type IconName } from "../../Icon/Icon.js";
import type { ResourceRef } from "../../Navigation/types.js";
import { ExternalLink } from "../../Prose/Prose.js";
import { Text } from "../../Text/Text.js";
import "./InlineLink.css";

// === Parsed target (what the href points at) ==================================
type InlineLinkResource = {
    readonly type: "resource";
    readonly ref: ResourceRef;
};
type InlineLinkExternal = { readonly type: "external"; readonly href: string };
type InlineLinkConversation = {
    readonly type: "conversation";
    readonly conversationId: string;
};
// Why the link degrades to text — dev-log vocabulary, never user-facing.
type InlineLinkPlain = {
    readonly type: "plain";
    readonly reason: "unknown_resource" | "unsupported_href";
};
type InlineLinkTarget =
    | InlineLinkResource
    | InlineLinkExternal
    | InlineLinkConversation
    | InlineLinkPlain;

type ResolveInlineLink = (href: string) => InlineLinkTarget;

// === View (what renders) ======================================================
type InlineLinkChip = {
    readonly type: "chip";
    readonly resourceRef: ComposerResourceRef;
    readonly onOpen: () => void;
};
// A chip labelled by the link's own text (the Journal's prose).
type InlineLinkTextChip = {
    readonly type: "text-chip";
    readonly glyph: IconName;
    readonly onOpen: () => void;
};
type InlineLinkExternalView = {
    readonly type: "external";
    readonly href: string;
};
type InlineLinkPlainView = { readonly type: "plain" };
type InlineLinkView =
    | InlineLinkChip
    | InlineLinkTextChip
    | InlineLinkExternalView
    | InlineLinkPlainView;

// A resource with no open handler wired renders as text (no dead chip button).
const selectInlineLinkView = (
    target: InlineLinkTarget,
    onOpenResource: ((ref: ResourceRef) => void) | undefined,
): InlineLinkView => {
    switch (target.type) {
        case "resource":
            return onOpenResource === undefined
                ? { type: "plain" }
                : {
                      type: "chip",
                      resourceRef: toComposerResourceRef(target.ref),
                      onOpen: () => onOpenResource(target.ref),
                  };
        case "external":
            return { type: "external", href: target.href };
        case "conversation":
        case "plain":
            return { type: "plain" };
        default:
            return exhaustSwitchCase(target);
    }
};

// One glyph per linkable kind (the composer's: a package borrows the folder).
const linkGlyph = (kind: ResourceRef["type"] | "conversation"): IconName => {
    switch (kind) {
        case "source":
            return "source";
        case "material":
            return "material";
        case "export":
            return "folder";
        case "file":
            return "file";
        case "conversation":
            return "chat";
        default:
            return exhaustSwitchCase(kind);
    }
};

// The Journal's links: resources AND conversations become chips labelled by
// the link text. A kind with no open handler renders as text.
const selectLinkTextChipView = (
    target: InlineLinkTarget,
    open: {
        readonly onOpenResource?: (ref: ResourceRef) => void;
        readonly onOpenConversation?: (id: string) => void;
    },
): InlineLinkView => {
    const { onOpenResource, onOpenConversation } = open;
    switch (target.type) {
        case "resource":
            return onOpenResource === undefined
                ? { type: "plain" }
                : {
                      type: "text-chip",
                      glyph: linkGlyph(target.ref.type),
                      onOpen: () => onOpenResource(target.ref),
                  };
        case "conversation":
            return onOpenConversation === undefined
                ? { type: "plain" }
                : {
                      type: "text-chip",
                      glyph: linkGlyph("conversation"),
                      onOpen: () => onOpenConversation(target.conversationId),
                  };
        case "external":
            return { type: "external", href: target.href };
        case "plain":
            return { type: "plain" };
        default:
            return exhaustSwitchCase(target);
    }
};

// The chip's look (the @-mention pill: glyph + one truncating line) around
// any label: the link's text in prose, or a token in the correction editor.
const LinkChip = ({
    glyph,
    children,
}: {
    readonly glyph: IconName;
    readonly children: ReactNode;
}): ReactNode => (
    <span className="bilda-composer-chip">
        <span className="bilda-composer-chip__glyph" aria-hidden="true">
            <Icon name={glyph} size="xs" />
        </span>
        <Text
            as="span"
            size="sm"
            weight="medium"
            className="bilda-composer-chip__title"
        >
            {children}
        </Text>
    </span>
);

// A link text's plain string (markdown hands over a string or an array of
// them): the chip's hover tooltip, since the visible label may truncate (D2).
const plainText = (node: ReactNode): string | undefined =>
    typeof node === "string" || typeof node === "number"
        ? String(node)
        : Array.isArray(node) && node.every((n) => typeof n === "string")
          ? node.join("")
          : undefined;

const InlineLink = ({
    view,
    children,
}: {
    readonly view: InlineLinkView;
    readonly children: ReactNode;
}): ReactNode => {
    switch (view.type) {
        case "chip":
            return (
                <button
                    type="button"
                    className="bilda-inline-link"
                    title={view.resourceRef.title}
                    onClick={view.onOpen}
                >
                    <ResourceMentionChip resourceRef={view.resourceRef} />
                </button>
            );
        case "text-chip":
            return (
                <button
                    type="button"
                    className="bilda-inline-link"
                    title={plainText(children)}
                    onClick={view.onOpen}
                >
                    <LinkChip glyph={view.glyph}>{children}</LinkChip>
                </button>
            );
        case "external":
            return <ExternalLink href={view.href}>{children}</ExternalLink>;
        case "plain":
            return <span>{children}</span>;
        default:
            return exhaustSwitchCase(view);
    }
};
